import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading } from './../../../../Common/Loading'
import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
import { Search } from './../../../../Common/Tables/Search'

import { LogErrors } from './../../../../../utilities/LogErrors'
import { ValidateSession } from './../../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../../Providers/FloatMessage'
import { GetCoursesForFilter, GetUsedLicensesReport } from './../../../../../utilities/BL/Instructor/Licenses'
import { WEBPAGES } from './../../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../../utilities/EL/Roles'

// Excel utils
import { BuildAndDownloadWorkbook, FormatDate, BuildSheetWithMeta } from './../../../../../utilities/BL/Instructor/ExcelExporter'

// Ancho y separación de cada columna de la tabla. Se definen aquí porque las clases
// heredadas de la página de Resultados están dimensionadas para otras columnas.
const COLUMN_STYLES = {
    course: { width: '240px', paddingRight: '20px' },
    student: { width: '260px', paddingRight: '20px' },
    user: { width: '140px', paddingRight: '20px' },
    group: { width: '260px', paddingRight: '20px' },
    date: { width: '160px' }
}

const UsedLicenses = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()
    const [isLoading, setIsLoading] = useState(true)
    const [isWorking, setIsWorking] = useState(false)

    // --- Catálogo del desplegable de filtro ---
    const [courses, setCourses] = useState([])
    const [selectedCourseId, setSelectedCourseId] = useState(0)

    // --- Filas de la tabla ---
    const [rows, setRows] = useState([])
    const [hasSearched, setHasSearched] = useState(false)

    // --- Búsqueda dentro de los resultados ya cargados ---
    const [searchText, setSearchText] = useState('')
    const [filteredRows, setFilteredRows] = useState([])

    useEffect(() => {
        const text = (searchText ?? '').toLowerCase()
        if (!text) { setFilteredRows(rows); return }

        const filtered = rows.filter(r => {
            const fields = [
                r?.courseName,
                r?.studentName,
                r?.user,
                r?.groupName,
                r?.registrationDate ? FormatDate(r.registrationDate) : ''
            ]
            return fields.some(v => String(v ?? '').toLowerCase().includes(text))
        })
        setFilteredRows(filtered)
    }, [searchText, rows])

    useEffect(() => {
        async function ValidateAndInit() {
            try {
                const tmpSession = await ValidateSession()
                if (tmpSession?.success && tmpSession.data?.user?.userStatus === 2) {
                    const resCourses = await GetCoursesForFilter()
                    if (resCourses.success) {
                        setCourses(resCourses.data)
                    } else {
                        showMessage({ type: MESSAGE_TYPE.BAD, text: resCourses.message || 'No fue posible cargar la información de los filtros.' })
                    }
                    setIsLoading(false)
                } else {
                    navigate('/confirm')
                }
            } catch (error) {
                await LogErrors('ValidateAndInit - Instructor UsedLicenses', error)
                navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
            }
        }
        ValidateAndInit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate])

    const HandleApplyFilter = async () => {
        try {
            if (!selectedCourseId) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona un curso.' })
                return
            }

            setIsWorking(true)
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo licencias utilizadas...' })

            const response = await GetUsedLicensesReport(selectedCourseId)

            setHasSearched(true)

            if (response.success) {
                setRows(response.data)
                if (response.data.length) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mostrando licencias utilizadas.' })
                } else {
                    showMessage({ type: MESSAGE_TYPE.INFO, text: 'No se encontraron licencias utilizadas con el curso seleccionado.' })
                }
            } else {
                setRows([])
                showMessage({ type: MESSAGE_TYPE.BAD, text: response.message || 'No ha sido posible obtener las licencias utilizadas.' })
            }
        } catch (error) {
            await LogErrors('HandleApplyFilter - Instructor UsedLicenses UI', error)
            setRows([])
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha ocurrido un error al aplicar el filtro.' })
        } finally {
            setIsWorking(false)
        }
    }

    const ExportFilteredUsedLicenses = async () => {
        try {
            setIsLoading(true)

            if (!filteredRows?.length) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay datos para exportar.' })
                return
            }

            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte...' })

            // Se toma de filteredRows (no del catálogo de filtros) porque ahí courseName ya
            // incluye el nivel del curso.
            const courseName = selectedCourseId
                ? (filteredRows.find(r => r.idCourse === selectedCourseId)?.courseName ?? 'Todos')
                : 'Todos'

            const rowsToExport = filteredRows.map(r => ({
                courseName: r.courseName,
                studentName: r.studentName,
                user: r.user,
                groupName: r.groupName,
                registrationDate: FormatDate(r.registrationDate)
            }))

            const headerOrder = ['courseName', 'studentName', 'user', 'groupName', 'registrationDate']
            const headerLabels = {
                courseName: 'Curso',
                studentName: 'Estudiante',
                user: 'Usuario',
                groupName: 'Grupo',
                registrationDate: 'Fecha'
            }
            const colWidths = [28, 30, 20, 30, 18]

            const metaRows = [
                ['Reporte:', 'Licencias utilizadas'],
                ['Curso:', courseName],
                ['Fecha de exportación:', new Date().toISOString().slice(0, 10)],
                ['Total registros:', rowsToExport.length]
            ]

            const ws = BuildSheetWithMeta({ metaRows, rows: rowsToExport, headerOrder, headerLabels, colWidths })

            BuildAndDownloadWorkbook(`Licencias_Utilizadas_${new Date().toISOString().slice(0, 10)}.xlsx`, [
                { name: 'Licencias Utilizadas', ws }
            ])

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
        } catch (error) {
            await LogErrors('ExportFilteredUsedLicenses - Instructor UsedLicenses UI', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible exportar el reporte solicitado.' })
        } finally {
            setIsLoading(false)
        }
    }

    const ExportAllUsedLicenses = async () => {
        try {
            setIsLoading(true)
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte...' })

            const response = await GetUsedLicensesReport()

            if (response.success) {
                if (!response.data?.length) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay licencias utilizadas para exportar.' })
                    return
                }

                const rowsToExport = response.data.map(r => ({
                    courseName: r.courseName,
                    studentName: r.studentName,
                    user: r.user,
                    groupName: r.groupName,
                    registrationDate: FormatDate(r.registrationDate)
                }))

                const headerOrder = ['courseName', 'studentName', 'user', 'groupName', 'registrationDate']
                const headerLabels = {
                    courseName: 'Curso',
                    studentName: 'Estudiante',
                    user: 'Usuario',
                    groupName: 'Grupo',
                    registrationDate: 'Fecha'
                }
                const colWidths = [28, 30, 20, 30, 18]

                const metaRows = [
                    ['Reporte:', 'Licencias utilizadas'],
                    ['Fecha de exportación:', new Date().toISOString().slice(0, 10)],
                    ['Total registros:', rowsToExport.length]
                ]

                const ws = BuildSheetWithMeta({ metaRows, rows: rowsToExport, headerOrder, headerLabels, colWidths })

                BuildAndDownloadWorkbook(`Licencias_Utilizadas_${new Date().toISOString().slice(0, 10)}.xlsx`, [
                    { name: 'Licencias Utilizadas', ws }
                ])

                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response.message || 'No ha sido posible exportar el reporte solicitado.' })
            }
        } catch (error) {
            await LogErrors('ExportAllUsedLicenses - Instructor UsedLicenses UI', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha ocurrido un error al exportar.' })
        } finally {
            setIsLoading(false)
        }
    }

    return isLoading ? (
        <Loading />
    ) : (
        <InstructorBase urlToGo={'/instructor/licenses'} page={WEBPAGES.INSTRUCTOR.DASHBOARD} role={ROLES.INSTRUCTOR}>
            <section className='container Instructor__Instructors Container-FullHeight'>
                <p className='Instructor__Title Instructor__Title--Center'>Licencias Utilizadas</p>

                <button type='button' className='Btn__Ztudia--Primary m-auto mt-4' onClick={ExportAllUsedLicenses}>
                    Exportar reporte completo
                </button>

                <p className='Instructor__Text--Center Instructor__SpaceUp'>----- O -----</p>

                {/* Filtro */}
                <div className='Instructor__Results__Reports__Filters Instructor__Results__Reports__Filters--Centered'>
                    {/* CURSO */}
                    <select
                        className='Instructor__Results__Reports__Filters__List'
                        value={selectedCourseId}
                        onChange={(e) => setSelectedCourseId(parseInt(e.target.value, 10))}
                        disabled={isWorking}
                    >
                        <option value={0}>Selecciona un curso</option>
                        {courses.map(course => (
                            <option key={course.idCourse} value={course.idCourse}>
                                {course.courseName}{course.level ? ` (Nivel ${course.level})` : ''}
                            </option>
                        ))}
                    </select>

                    {/* APLICAR FILTRO */}
                    <button
                        type='button'
                        className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__Filters__Button'
                        onClick={HandleApplyFilter}
                        disabled={isWorking || !selectedCourseId}
                    >
                        <i className='bi bi-funnel'></i> Aplicar filtro
                    </button>
                </div>

                {/* Buscador + Export */}
                <Search onChange={setSearchText} extraClassName={'Instructor__Results--V2__Search'} />
                <button
                    type='button'
                    className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__ExportButton'
                    onClick={ExportFilteredUsedLicenses}
                    disabled={!filteredRows?.length}
                >
                    <i className='bi bi-filetype-xlsx'></i> Exportar
                </button>

                {/* Tabla de licencias utilizadas */}
                <section className='Tables__Container Instructor__Results--V2__Reports__ContainerTable Tables--Shadow Tables--Background'>
                    <table className='Tables__Table Instructor__Results--V2__Reports__ContainerTable__Table'>
                        <thead>
                            <tr className='Tables__Table__Row--Header'>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn' style={COLUMN_STYLES.course}>Curso</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn' style={COLUMN_STYLES.student}>Estudiante</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn' style={COLUMN_STYLES.user}>Usuario</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn' style={COLUMN_STYLES.group}>Grupo</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn' style={COLUMN_STYLES.date}>Fecha</td>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows.length ? (
                                filteredRows.map((r, index) => (
                                    <tr key={index} className='Tables__Table__Row'>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn' style={COLUMN_STYLES.course}>{r.courseName || '-'}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn' style={COLUMN_STYLES.student}>{r.studentName || '-'}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn' style={COLUMN_STYLES.user}>{r.user || '-'}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn' style={COLUMN_STYLES.group}>{r.groupName || '-'}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn' style={COLUMN_STYLES.date}>{r.registrationDate ? FormatDate(r.registrationDate) : '-'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr className='Tables__Table__Row'>
                                    <td className='Tables__Table__Row__Cell' colSpan={5}>
                                        {isWorking
                                            ? 'Cargando...'
                                            : rows.length
                                                ? 'No hay coincidencias para la búsqueda.'
                                                : hasSearched
                                                    ? 'No se encontraron licencias utilizadas con los filtros seleccionados.'
                                                    : 'Sin resultados por mostrar.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>
            </section>
        </InstructorBase>
    )
}

export { UsedLicenses };
